import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, Plug, BookOpen, AlertTriangle, LogOut } from "lucide-react";
import * as api from "../../../lib/api";
import { Card, Field, SecretField, Label, Hint, ResultBanner, PRIMARY, inputCls, ringStyle, useSettings } from "../shared";
import { CmsLogo, StatusBadge, cmsById, cmsStatus } from "./catalog";

// Default config per CMS — mirrors AstroConfig / WebflowConfig in
// backend/services/cms/connections.py.
const DEFAULT_CONFIG = {
  astro: { repo_owner: "", repo_name: "", branch: "main", content_dir: "src/content/blog", frontmatter_map: {} },
  webflow: { site_id: "", site_name: "", collections: [], field_map: {} },
};
// The one secret each CMS needs (backend PRIMARY_SECRET).
const SECRET_KEY = { astro: "pat", webflow: "api_token" };

const OAUTH_ERRORS = {
  invalid_state: "The Webflow login link expired or was tampered with — try again.",
  access_denied: "Webflow access was declined.",
  token_exchange_failed: "Webflow didn't accept the login — try again.",
  server_misconfigured: "The server can't store credentials (CMS_SECRET_KEY missing).",
  missing_code: "Webflow didn't return a login code — try again.",
  invalid_scope: "The Webflow app doesn't grant the requested scopes — check its Building blocks (Sites, CMS, Pages).",
};

const errMsg = (err, fallback) => err.response?.data?.detail || err.message || fallback;

// Guide + connect form for one CMS (/dashboard/settings/cms/:cmsId).
// One connection per org: saving here replaces a connection to another CMS.
export default function CmsDetailSection() {
  const { cmsId } = useParams();
  const cms = cmsById(cmsId);
  const { cmsConn, refreshCms, loading } = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();

  const sameType = cmsConn?.cms_type === cmsId;
  const otherCms = cmsConn?.cms_type && !sameType ? cmsById(cmsConn.cms_type) : null;
  const savedConfig = useMemo(
    () => (sameType ? { ...DEFAULT_CONFIG[cmsId], ...cmsConn.config } : DEFAULT_CONFIG[cmsId]),
    [sameType, cmsId, cmsConn?.config],
  );

  const [draft, setDraft] = useState(savedConfig);
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(null); // "save" | "test" | "disconnect"
  const [result, setResult] = useState(null);
  const [banner, setBanner] = useState(null);
  const [collections, setCollections] = useState(null);

  useEffect(() => { setDraft(savedConfig); setSecret(""); }, [savedConfig]);

  // Returning from Webflow's OAuth screen.
  useEffect(() => {
    const ok = searchParams.get("webflow_connected");
    const error = searchParams.get("webflow_error");
    const description = searchParams.get("webflow_error_description");
    if (!ok && !error) return;
    if (ok) console.log("[webflow:oauth] connected");
    else console.error("[webflow:oauth] failed:", error, description || "");
    setBanner(ok
      ? { success: true, message: "Connected to Webflow. Confirm your collections below and save." }
      : { success: false, message: `${OAUTH_ERRORS[error] || `Webflow connection failed (${error}).`}${description ? ` ${description}` : ""}` });
    setSearchParams({}, { replace: true });
    if (ok) refreshCms().catch(console.error);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cms) return <Navigate to=".." relative="path" replace />;

  const status = cmsStatus(cms, cmsConn);
  const dirty = !!secret || JSON.stringify(draft) !== JSON.stringify(savedConfig);
  const secretPayload = secret ? { [SECRET_KEY[cms.id]]: secret } : null;

  const save = async () => {
    if (otherCms && !window.confirm(`Your organization syncs from ${otherCms.name}. Saving replaces that connection with ${cms.name}. Continue?`)) return;
    setBusy("save");
    setResult(null);
    try {
      await api.saveCmsConnection({ cms_type: cms.id, config: draft, secret: secretPayload });
      await refreshCms();
      setResult({ success: true, message: "Connection saved." });
    } catch (err) {
      setResult({ success: false, message: errMsg(err, "Failed to save connection.") });
    } finally {
      setBusy(null);
    }
  };

  // Unsaved edits are tested as-is; a clean form tests (and records status for) the saved connection.
  const test = async () => {
    setBusy("test");
    setResult(null);
    try {
      const payload = dirty || !sameType ? { cms_type: cms.id, config: draft, secret: secretPayload } : {};
      setResult(await api.testCmsConnection(payload));
      if (!dirty && sameType) await refreshCms();
    } catch (err) {
      setResult({ success: false, message: errMsg(err, "Connection test failed.") });
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(`Disconnect ${cms.name}? Syncing stops until you connect a CMS again. Synced articles stay.`)) return;
    setBusy("disconnect");
    try {
      await api.deleteCmsConnection();
      await refreshCms();
      setCollections(null);
      setResult({ success: true, message: `${cms.name} disconnected.` });
    } catch (err) {
      setResult({ success: false, message: errMsg(err, "Failed to disconnect.") });
    } finally {
      setBusy(null);
    }
  };

  const canSave = cms.id === "astro"
    ? draft.repo_owner && draft.repo_name && (secret || (sameType && cmsConn.has_secret))
    : (secret || (sameType && cmsConn.has_secret));
  const hasArticles = cms.id !== "webflow" || (draft.collections || []).some(c => c.role === "articles");

  return (
    <div className="space-y-4" data-testid={`cms-detail-${cms.id}`}>
      <Link to=".." relative="path" className="inline-flex items-center gap-1 text-[11px] font-bold text-zinc-500 hover:text-zinc-900">
        <ArrowLeft size={12} /> All CMS integrations
      </Link>

      <div className="bg-white rounded-xl border border-zinc-100 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <CmsLogo cms={cms} size={52} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-bold text-zinc-900 tracking-tight">{cms.name}</h2>
            {!loading && <StatusBadge status={status} />}
          </div>
          <p className="text-xs text-zinc-500 mt-0.5">{cms.tagline}</p>
          <a href={cms.docsUrl} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-zinc-500 underline underline-offset-4 decoration-zinc-300 hover:text-zinc-900 mt-1"
          >
            {cms.name} documentation <ArrowUpRight size={11} />
          </a>
        </div>
        {sameType && (
          <button type="button" onClick={disconnect} disabled={!!busy} data-testid="cms-disconnect-button"
            className="inline-flex items-center gap-1 px-3 py-2 text-[11px] font-bold border border-zinc-100 text-zinc-500 hover:text-red-600 hover:border-red-200 rounded-lg disabled:opacity-40">
            <LogOut size={12} /> Disconnect
          </button>
        )}
      </div>

      <ResultBanner result={banner} testId="cms-oauth-banner" />
      {otherCms && (
        <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          Your organization currently syncs from <span className="font-bold">{otherCms.name}</span>. Each organization uses one CMS — saving a {cms.name} connection replaces it. Articles already synced stay until you remove them.
        </p>
      )}
      {sameType && cmsConn.status === "error" && cmsConn.last_error && (
        <p className="text-[10px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">Last test failed: {cmsConn.last_error}</p>
      )}

      <Card icon={BookOpen} title="How to connect" subtitle={`${cms.steps.length} steps`}>
        <ol className="space-y-5">
          {cms.steps.map((step, i) => (
            <li key={step.title} className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full text-[11px] font-bold text-white flex items-center justify-center"
                style={{ backgroundColor: cms.color }}>
                {i + 1}
              </span>
              <div className="min-w-0 flex-1 space-y-2">
                <p className="text-sm font-bold text-zinc-900">{step.title}</p>
                <p className="text-xs text-zinc-600 leading-relaxed">{step.body}</p>
                {step.code && (
                  <pre className="text-[11px] bg-zinc-50 border border-zinc-100 rounded-lg px-3 py-2.5 overflow-x-auto font-mono text-zinc-700">{step.code}</pre>
                )}
                {step.link && (
                  <a href={step.link.href} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-zinc-900 underline underline-offset-4 decoration-zinc-300">
                    {step.link.label} <ArrowUpRight size={11} />
                  </a>
                )}
                {step.sitePrompt && (
                  <Link to="../../site" relative="path"
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-zinc-900 underline underline-offset-4 decoration-zinc-300">
                    Open Site &amp; Sync <ArrowUpRight size={11} />
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ol>
      </Card>

      <Card icon={Plug} title="Connection" subtitle={`${cms.name} credentials`}>
        {cms.id === "webflow" ? (
          <WebflowFields
            conn={sameType ? cmsConn : null} oauthAvailable={!!cmsConn?.oauth?.webflow}
            draft={draft} setDraft={setDraft} secret={secret} setSecret={setSecret}
            collections={collections} setCollections={setCollections}
          />
        ) : (
          <AstroFields conn={sameType ? cmsConn : null} draft={draft} setDraft={setDraft} secret={secret} setSecret={setSecret} />
        )}

        <ResultBanner result={result} testId="cms-connection-result" />
        <div className="flex flex-col sm:flex-row gap-2">
          <button type="button" onClick={test} disabled={!!busy || !canSave || !hasArticles}
            data-testid="test-connection-button"
            className="flex-1 px-3 py-2 text-xs font-bold border border-zinc-100 text-zinc-900 hover:bg-zinc-50 disabled:opacity-40 transition-colors rounded-lg"
          >
            {busy === "test" ? "Testing..." : "Test Connection"}
          </button>
          <button type="button" onClick={save} disabled={!!busy || !canSave || (sameType && !dirty)}
            data-testid="cms-save-button"
            className="flex-1 px-3 py-2 text-xs font-bold text-white disabled:opacity-40 transition-colors rounded-lg hover:opacity-90"
            style={{ backgroundColor: PRIMARY }}
          >
            {busy === "save" ? "Saving..." : sameType ? "Save changes" : `Use ${cms.name}`}
          </button>
        </div>
        {dirty && sameType && <p className="text-[10px] text-amber-600 font-bold text-right">Unsaved changes</p>}
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-white rounded-xl border border-zinc-100 p-4">
          <Label className="mb-2">What it does</Label>
          <ul className="space-y-1.5">
            {cms.capabilities.map(c => (
              <li key={c} className="flex gap-1.5 text-[11px] text-zinc-600"><Check size={12} className="text-green-600 flex-shrink-0 mt-0.5" />{c}</li>
            ))}
          </ul>
        </div>
        <div className="bg-white rounded-xl border border-zinc-100 p-4">
          <Label className="mb-2">Good to know</Label>
          <ul className="space-y-1.5">
            {cms.limitations.map(c => (
              <li key={c} className="flex gap-1.5 text-[11px] text-zinc-600"><AlertTriangle size={12} className="text-amber-500 flex-shrink-0 mt-0.5" />{c}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function SavedSecretHint({ conn }) {
  if (!conn?.has_secret) return null;
  return <Hint>Saved ({conn.secret_hint}). Leave blank to keep it.</Hint>;
}

function AstroFields({ conn, draft, setDraft, secret, setSecret }) {
  const set = e => setDraft(d => ({ ...d, [e.target.name]: e.target.value }));
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Repo Owner" required name="repo_owner" value={draft.repo_owner} onChange={set}
          placeholder="your-org" data-testid="github-repo-owner-input" />
        <Field label="Repo Name" required name="repo_name" value={draft.repo_name} onChange={set}
          placeholder="my-astro-site" data-testid="github-repo-name-input" />
        <Field label="Branch" name="branch" value={draft.branch} onChange={set}
          placeholder="main" data-testid="github-branch-input" />
        <Field label="Content Dir" mono name="content_dir" value={draft.content_dir} onChange={set}
          placeholder="src/content/blog" data-testid="github-content-dir-input" />
      </div>
      <div>
        <SecretField label="Personal Access Token" required={!conn?.has_secret} name="pat" value={secret} onChange={e => setSecret(e.target.value)}
          placeholder={conn?.has_secret ? "•••••••• (saved)" : "github_pat_..."} data-testid="github-pat-input" autoComplete="off" />
        {conn?.has_secret ? <SavedSecretHint conn={conn} /> : <Hint>Fine-grained PAT scoped to this repo, with read/write access to contents.</Hint>}
      </div>
    </>
  );
}

const ROLE_OPTIONS = [
  { value: "articles", label: "Articles" },
  { value: "commercial", label: "Commercial pages" },
  { value: "ignore", label: "Ignore" },
];

function WebflowFields({ conn, oauthAvailable, draft, setDraft, secret, setSecret, collections, setCollections }) {
  const [connecting, setConnecting] = useState(false);
  const [loadingCols, setLoadingCols] = useState(false);
  const [colsError, setColsError] = useState(null);
  const [showToken, setShowToken] = useState(!oauthAvailable);

  const startOauth = async () => {
    setConnecting(true);
    try {
      const { url } = await api.webflowOauthAuthorize(window.location.origin + window.location.pathname);
      const parsed = new URL(url);
      console.log("[webflow:oauth] redirecting", {
        redirect_uri: parsed.searchParams.get("redirect_uri"),
        scope: parsed.searchParams.get("scope"),
      });
      window.location.href = url;
    } catch (err) {
      console.error("[webflow:oauth] authorize failed:", err.response?.status, err.response?.data);
      setColsError(errMsg(err, "Couldn't start the Webflow login."));
      setConnecting(false);
    }
  };

  // Merge live collections with saved roles; unsaved collections take the suggested role.
  const loadCollections = async () => {
    setLoadingCols(true);
    setColsError(null);
    try {
      const res = await api.listWebflowCollections(secret || null);
      if (!res.success) {
        setColsError(res.message);
        return;
      }
      setCollections(res.collections);
      setDraft(d => {
        const saved = Object.fromEntries((d.collections || []).map(c => [c.id, c.role]));
        const merged = res.collections.map(c => ({ id: c.id, name: c.name, slug: c.slug, role: saved[c.id] || c.suggested_role }));
        const articles = res.collections.find(c => (saved[c.id] || c.suggested_role) === "articles");
        return { ...d, collections: merged, site_id: articles?.site_id || d.site_id, site_name: articles?.site_name || d.site_name };
      });
    } catch (err) {
      setColsError(errMsg(err, "Couldn't load collections."));
    } finally {
      setLoadingCols(false);
    }
  };

  // Auto-load once credentials are saved (e.g. right after the OAuth redirect).
  useEffect(() => {
    if (conn?.has_secret && !collections) loadCollections();
  }, [conn?.has_secret, conn?.secret_hint]); // eslint-disable-line react-hooks/exhaustive-deps

  const setRole = (id, role) => setDraft(d => ({
    ...d,
    collections: (d.collections || []).map(c =>
      c.id === id ? { ...c, role } : (role === "articles" && c.role === "articles" ? { ...c, role: "ignore" } : c)),
  }));

  const siteOf = id => collections?.find(c => c.id === id)?.site_name;
  const countOf = id => {
    const n = collections?.find(c => c.id === id)?.item_count;
    return n == null ? "" : ` · ${n} item${n === 1 ? "" : "s"}`;
  };
  const rows = draft.collections || [];

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        {oauthAvailable ? (
          <button type="button" onClick={startOauth} disabled={connecting} data-testid="webflow-oauth-button"
            className="px-4 py-2.5 text-xs font-bold text-white rounded-lg hover:opacity-90 disabled:opacity-40"
            style={{ backgroundColor: "#146EF5" }}>
            {connecting ? "Opening Webflow…" : conn?.auth_method === "oauth" ? "Reconnect Webflow" : "Connect with Webflow"}
          </button>
        ) : (
          <p className="text-[11px] text-zinc-500">Connect with Webflow isn't enabled on this server — use a site token.</p>
        )}
        {conn?.has_secret && (
          <p className="text-[11px] text-zinc-600">
            <Check size={12} className="inline text-green-600 mr-1" />
            {conn.auth_method === "oauth" ? `Connected${conn.connected_as ? ` as ${conn.connected_as}` : ""}` : `Site token saved (${conn.secret_hint})`}
          </p>
        )}
      </div>

      {oauthAvailable && (
        <button type="button" onClick={() => setShowToken(v => !v)} className="text-[10px] text-zinc-500 hover:text-zinc-900 text-left">
          {showToken ? "Hide site token option" : "Advanced: use a site API token instead"}
        </button>
      )}
      {showToken && (
        <div>
          <SecretField label="Site API Token" name="api_token" value={secret} onChange={e => setSecret(e.target.value)}
            placeholder={conn?.has_secret ? "•••••••• (saved)" : "Site API token"} data-testid="webflow-api-token-input" autoComplete="off" />
          {conn?.has_secret ? <SavedSecretHint conn={conn} /> : <Hint>Site token with CMS: Read and write and Sites: Read-only scopes.</Hint>}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <Label className="mb-0">Collections</Label>
          <button type="button" onClick={loadCollections} disabled={loadingCols || (!secret && !conn?.has_secret)}
            data-testid="webflow-load-collections"
            className="px-2.5 py-1 text-[10px] font-bold border border-zinc-100 text-zinc-900 hover:bg-zinc-50 disabled:opacity-40 rounded-lg">
            {loadingCols ? "Loading…" : collections ? "Reload" : "Load collections"}
          </button>
        </div>
        {colsError && <p className="text-[10px] text-red-500 mb-1">{colsError}</p>}
        {rows.length ? (
          <div className="border border-zinc-100 rounded-lg divide-y divide-zinc-100" data-testid="webflow-collections">
            {rows.map(c => (
              <div key={c.id} className="flex items-center gap-3 px-3 py-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-zinc-900 truncate">{c.name || c.id}</p>
                  <p className="text-[10px] text-zinc-400 font-mono truncate">{siteOf(c.id) ? `${siteOf(c.id)} · ` : ""}/{c.slug}{countOf(c.id)}</p>
                </div>
                <select value={c.role} onChange={e => setRole(c.id, e.target.value)} data-testid={`webflow-role-${c.id}`}
                  className={`${inputCls} !w-44 !py-1.5 text-xs`} style={ringStyle}>
                  {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            ))}
          </div>
        ) : (
          <Hint>{conn?.has_secret || secret ? "Load collections to pick which one holds your articles." : "Connect first — then your collections appear here."}</Hint>
        )}
        {rows.length > 0 && !rows.some(c => c.role === "articles") && (
          <p className="text-[10px] text-amber-700 mt-1">Pick one collection as Articles — that's what syncs.</p>
        )}
      </div>
    </>
  );
}
